import React, { useState, useEffect } from "react";
import { Input, Button, Card, Modal, Form, Table, Popconfirm, Tag, Spin, Select } from "antd";
import { Search, Plus, Edit, Trash2, TestTube } from 'lucide-react';
import Sidebar from "../../GlobalFiles/Sidebar";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import axios from "axios";
import { API_BASE_URL } from "../../../../../api";
import SuperGroupSelector from "../../../../../Components/Dropdowns/SuperGroupSelector";

const TestCategoryManagement = () => {
  const [loading, setLoading] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [categories, setCategories] = useState([]);
  const [filteredCategories, setFilteredCategories] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [form] = Form.useForm();

  const fetchCategories = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE_URL}/tests/categories`);
      setCategories(response.data || []);
      setFilteredCategories(response.data || []);
      console.log("Fetched categories:", response.data);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching categories:", error);
      toast.error("Failed to fetch categories. Please try again.", { position: "top-right", autoClose: 4000 });
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  useEffect(() => {
    const term = (searchTerm || "").toLowerCase();
    if (!term) return setFilteredCategories(categories);
    setFilteredCategories(
      categories.filter(cat =>
        (cat.superGroup || "").toLowerCase().includes(term) ||
        (cat.categoryName || "").toLowerCase().includes(term) ||
        (cat.categoryId || "").toString().includes(term)
      )
    );
  }, [searchTerm, categories]);

  const openCreateModal = () => {
    setEditingCategory(null);
    form.resetFields();
    setIsModalVisible(true);
  };

  const handleSubmit = async (values) => {
    try {
      setLoading(true);
      if (editingCategory) {
        await axios.put(`${API_BASE_URL}/tests/categories/${editingCategory._id}`, {
          categoryName: values.categoryName,
          isActive: values.isActive,
          superGroup: values.superGroup,
          updatedAt: Date.now()
        });

        const newDoctor = (values.doctorCommissionPercentage === "" || values.doctorCommissionPercentage === null) ? undefined : values.doctorCommissionPercentage;
        const newAgent = (values.agentCommissionPercentage === "" || values.agentCommissionPercentage === null) ? undefined : values.agentCommissionPercentage;

        const doctorChanged = newDoctor !== undefined && Number(newDoctor) !== Number(editingCategory.doctorCommissionPercentage || 0);
        const agentChanged = newAgent !== undefined && Number(newAgent) !== Number(editingCategory.agentCommissionPercentage || 0);

        if ((doctorChanged || agentChanged) && (values.superGroup || editingCategory.superGroup)) {
          const body = { superGroup: values.superGroup || editingCategory.superGroup };
          if (doctorChanged) body.doctorCommissionPercentage = Number(newDoctor);
          if (agentChanged) body.agentCommissionPercentage = Number(newAgent);

          await axios.patch(`${API_BASE_URL}/tests/update-commissions-percentage-by-supergroup`, body);
          toast.success("Category updated and superGroup commission(s) applied successfully!", { position: "top-right", autoClose: 3000 });
        } else {
          toast.success("Category updated successfully!", { position: "top-right", autoClose: 3000 });
        }
      } else {
        await axios.post(`${API_BASE_URL}/tests/categories`, {
          categoryId: values.categoryId,
          categoryName: values.categoryName,
          superGroup: values.superGroup,
          doctorCommissionPercentage: values.doctorCommissionPercentage,
          agentCommissionPercentage: values.agentCommissionPercentage,
        });
        toast.success("Category created successfully!", { position: "top-right", autoClose: 3000 });
      }

      setIsModalVisible(false);
      setEditingCategory(null);
      form.resetFields();
      await fetchCategories();
      setLoading(false);
    } catch (error) {
      console.error("Error saving category:", error);
      toast.error(error.response?.data?.message || "Failed to save category. Please try again.", { position: "top-right", autoClose: 4000 });
      setLoading(false);
    }
  };

  const handleEdit = (category) => {
    setEditingCategory(category);
    form.setFieldsValue({
      categoryId: category.categoryId,
      categoryName: category.categoryName,
      superGroup: category.superGroup || "",
      isActive: category.isActive,
      doctorCommissionPercentage: category.doctorCommissionPercentage,
      agentCommissionPercentage: category.agentCommissionPercentage,
    });
    setIsModalVisible(true);
  };

  const handleToggleActive = async (category) => {
    try {
      setLoading(true);
      const isCurrentlyActive = category.isActive !== false;
      await axios.put(`${API_BASE_URL}/tests/categories/${category._id}`, {
        categoryName: category.categoryName,
        superGroup: category.superGroup,
        isActive: !isCurrentlyActive,
        updatedAt: Date.now()
      });
      toast.success(`Category ${!isCurrentlyActive ? 'activated' : 'deactivated'} successfully!`, { position: "top-right", autoClose: 2500 });
      await fetchCategories();
      setLoading(false);
    } catch (error) {
      console.error("Error toggling category active state:", error);
      toast.error("Failed to update category status.", { position: "top-right", autoClose: 3000 });
      setLoading(false);
    }
  };

  const columns = [
    {
      title: 'Category ID',
      dataIndex: 'categoryId',
      key: 'categoryId',
      width: 120,
      sorter: (a, b) => (a.categoryId || '').localeCompare(b.categoryId || ''),
    },
    {
      title: 'Category Name',
      dataIndex: 'categoryName',
      key: 'categoryName',
      sorter: (a, b) => (a.categoryName || '').localeCompare(b.categoryName || ''),
    },
    {
      title: 'SuperGroup',
      dataIndex: 'superGroup',
      key: 'superGroup',
      width: 160,
      render: (sg) => sg ? <Tag color="purple">{sg}</Tag> : <Tag color="default">-</Tag>,
      sorter: (a, b) => (a.superGroup || '').localeCompare(b.superGroup || ''),
    },
    {
      title: 'Status',
      dataIndex: 'isActive',
      key: 'isActive',
      width: 150,
      render: (isActive, record) => {
        const active = isActive !== false;
        return (
          <div className="flex items-center space-x-2">
            <button
              onClick={() => handleToggleActive(record)}
              title={active ? 'Click to Disable' : 'Click to Enable'}
              className={`relative inline-flex h-5 w-10 items-center rounded-full transition-colors focus:outline-none shadow-inner ${
                !active ? 'bg-gray-300 hover:bg-gray-400' : 'bg-emerald-500 hover:bg-emerald-600'
              }`}
            >
              <span
                className="flex items-center justify-center h-4 w-4 transform rounded-full bg-white shadow-sm transition-transform"
                style={{ transform: !active ? 'translateX(2px)' : 'translateX(22px)' }}
              />
            </button>
            <span className={`text-xs font-semibold ${!active ? 'text-gray-500' : 'text-emerald-600'}`}>
              {active ? 'Active' : 'Inactive'}
            </span>
          </div>
        );
      },
      filters: [
        { text: 'Active', value: true },
        { text: 'Inactive', value: false },
      ],
      onFilter: (value, record) => (record.isActive !== false) === value,
    },
    {
      title: 'Total Tests',
      dataIndex: 'tests',
      key: 'tests',
      render: (d) => d ? d.length : 0,
      sorter: (a, b) => (a.tests?.length || 0) - (b.tests?.length || 0),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 100,
      render: (_, record) => (
        <div className="flex gap-2">
          <Button type="link" icon={<Edit className="w-4 h-4" />} onClick={() => handleEdit(record)} size="small" />
        </div>
      ),
    },
  ];

  return (
    <div>
      <div className="flex min-h-screen bg-gray-50">
        <Sidebar onCollapse={setSidebarCollapsed} />
        <div className={`flex-1 p-6 transition-all duration-300 ${sidebarCollapsed ? 'ml-20' : 'ml-0'}`}>
          <div className="flex-1 p-6 ml-40">
            <Card className="mb-6 shadow-sm border border-gray-200">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <TestTube className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <h1 className="text-2xl font-bold text-gray-900">Test Group Management</h1>
                    <p className="text-gray-600">Manage test groups (create, edit, activate/deactivate)</p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button onClick={openCreateModal} className="flex items-center gap-2 px-4 py-2 font-semibold rounded-lg shadow-md transition-all duration-200 bg-gradient-to-r from-blue-500 to-purple-500 text-white hover:from-blue-600 hover:to-purple-600">
                    <Plus className="w-4 h-4" /> Add New Group
                  </Button>
                </div>
              </div>
            </Card>

            {/* Filters */}
            <Card className="mb-6 shadow-sm border border-gray-200">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Search Categories</label>
                  <Input prefix={<Search className="w-4 h-4 text-gray-400" />} placeholder="Search by name or ID..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="border-gray-200 focus:ring-blue-500" />
                </div>
                <div />
                <div className="flex items-end">
                  <div className="bg-blue-50 rounded-lg p-4 w-full">
                    <div className="text-sm text-gray-600">Total Groups</div>
                    <div className="text-2xl font-bold text-blue-600">{filteredCategories.length}</div>
                  </div>
                </div>
              </div>
            </Card>

            {/* Categories Table */}
            <Card className="shadow-sm border border-gray-200">
              <Spin spinning={loading}>
                <Table 
                    columns={columns} 
                    dataSource={filteredCategories} 
                    rowKey="_id" 
                    pagination={{ pageSize: 20 }} 
                    scroll={{ x: 900 }} 
                    className="ant-table-striped" 
                    />
              </Spin>
            </Card>
          </div>
        </div>
      </div>

      {/* Add/Edit Category Modal */}
      <Modal title={editingCategory ? "Edit Category" : "Add New Category"} open={isModalVisible} onCancel={() => { setIsModalVisible(false); setEditingCategory(null); form.resetFields(); }} footer={null} width={600}>
        <Form form={form} layout="vertical" onFinish={handleSubmit} className="mt-4">
          <Form.Item label="Category ID" name="categoryId" rules={[{ required: true, message: "Please enter category ID" }]}> 
            <Input placeholder="e.g. BIO" disabled={!!editingCategory} />
          </Form.Item>
          <Form.Item label="Category Name" name="categoryName" rules={[{ required: true, message: "Please enter category name" }]}> 
            <Input placeholder="Enter category name" />
          </Form.Item>
          {/* <Form.Item
            label="Doctor Commission (%)"
            name="doctorCommissionPercentage"
            rules={[
              {
                validator: (_, value) => {
                  if (value === undefined || value === null || value === "") return Promise.resolve();
                  const num = Number(value);
                  if (isNaN(num) || num < 0 || num > 100) {
                    return Promise.reject(new Error('Must be between 0 and 100'));
                  }
                  return Promise.resolve();
                }
              }
            ]}
          >
            <Input type="number" step="1" placeholder="Enter doctor commission percentage" />
          </Form.Item>  */}
          {/* <Form.Item
            label="Agent Commission (%)"
            name="agentCommissionPercentage"
            rules={[
              {
                validator: (_, value) => {
                  if (value === undefined || value === null || value === "") return Promise.resolve();
                  const num = Number(value);
                  if (isNaN(num) || num < 0 || num > 100) {
                    return Promise.reject(new Error('Must be between 0 and 100'));
                  }
                  return Promise.resolve();
                }
              }
            ]}
          >
            <Input type="number" step="1" placeholder="Enter agent commission percentage" />
          </Form.Item> */}
          <Form.Item label="SuperGroup" name="superGroup"> 
            <SuperGroupSelector
              value={form.getFieldValue('superGroup')}
              onChange={(val) => form.setFieldsValue({ superGroup: val })}
              placeholder="Select Super Group"
              style={{ width: '100%', marginTop: '8px' }}
            />
          </Form.Item>
          <Form.Item label="Status" name="isActive" initialValue={true}> 
            <Select>
              <Select.Option value={true}>Active</Select.Option>
              <Select.Option value={false}>Inactive</Select.Option>
            </Select>
          </Form.Item>

          <div className="flex justify-end gap-2 mt-6">
            <Button onClick={() => { setIsModalVisible(false); setEditingCategory(null); form.resetFields(); }}>Cancel</Button>
            <Button type="primary" htmlType="submit" loading={loading} className="bg-blue-600 hover:bg-blue-700">{editingCategory ? "Update Category" : "Create Category"}</Button>
          </div>
        </Form>
      </Modal>
    </div>
  );
};

export default TestCategoryManagement;